import { Container, Divider, Typography } from '@cleartrip/ct-design-components'
import React, { memo } from 'react'

import RevampTooltip from './RevampTooltip'
import styles from './styles.module.scss'
import { TUPLE_COLORS, TUPLE_HOVER_UNDERLINE_CSS } from '../config/tupleTheme'
import type { TupleStop } from '../model/types'

export interface DurationStopsBlockProps {
  durationLabel: string
  stop: TupleStop
  density?: 'default' | 'compact'
}

const stopTone = (stopCount: number) => {
  if (stopCount === 0) return TUPLE_COLORS.success
  if (stopCount === 1) return TUPLE_COLORS.warningLine
  return TUPLE_COLORS.alert500
}

const DOT_SIZE = 3
const LINE_WIDTH = 80
/** Gap between the two centered stop dots (px). */
const STOP_DOT_GAP = 17

const CSS_COMPACT = { width: 96, minWidth: 96 }
const CSS_DEFAULT = { width: 120, minWidth: 120 }
const CSS_LINE = { height: 1, width: LINE_WIDTH }
/** Capped at the block width so any stop label that doesn't fit ellipsizes instead of spilling into the times. */
const CSS_STOP_LABEL_WRAP = { maxWidth: '100%', minWidth: 0, width: 'fit-content' }
const STOP_LABEL_STYLE = { maxWidth: '100%', minWidth: 0 }

/** Two dots centered on the line with STOP_DOT_GAP between centers. */
const TWO_DOT_LEFTS = [
  Math.round(((LINE_WIDTH / 2 - STOP_DOT_GAP / 2) / LINE_WIDTH) * 100),
  Math.round(((LINE_WIDTH / 2 + STOP_DOT_GAP / 2) / LINE_WIDTH) * 100),
] as const

const stopDotPositions = (stopCount: number): number[] => {
  if (stopCount <= 0) return []
  if (stopCount === 1) return [50]
  return [...TWO_DOT_LEFTS]
}

const DOT_TONES = [TUPLE_COLORS.success, TUPLE_COLORS.warningLine, TUPLE_COLORS.alert500] as const
const DOT_LEFTS = [50, ...TWO_DOT_LEFTS] as const

const DOT_CSS: Record<string, object> = Object.fromEntries(
  DOT_TONES.flatMap(tone =>
    DOT_LEFTS.map(leftPercent => [
      `${tone}-${leftPercent}`,
      {
        border: `1px solid ${tone}`,
        boxSizing: 'content-box',
        height: DOT_SIZE,
        left: `${leftPercent}%`,
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: DOT_SIZE,
      },
    ])
  )
)

const DurationStopsBlock: React.FC<DurationStopsBlockProps> = ({ density = 'default', durationLabel, stop }) => {
  const tone = stopTone(stop.count)
  const dots = stopDotPositions(stop.count)
  const isCompact = density === 'compact'
  const layovers = stop.layovers?.filter(Boolean) ?? []
  const showUnderline = stop.count > 0
  const lineCss = { ...CSS_LINE, backgroundColor: tone }
  const hoverBlockClass = showUnderline ? styles.durationStopsHover : ''
  const hoverTextClass = showUnderline ? styles.durationStopsHoverText : ''
  const durationClassName = `ta-center ws-nowrap ${hoverTextClass}`
  const stopLabelClassName = `ta-center ws-nowrap tt-ellipsis o-hidden ${hoverTextClass}`

  const body = (
    <Container
      className={`flex flex-column flex-middle flex-center flex-rg-1 flex-no-shrink ${hoverBlockClass}`}
      css={isCompact ? CSS_COMPACT : CSS_DEFAULT}
    >
      <Typography
        className={durationClassName}
        color='heading'
        componentNode='p'
        variant='B3'
      >
        {durationLabel}
      </Typography>
      <Container className='p-relative' css={lineCss}>
        {dots.map(leftPercent => (
          <Container key={leftPercent} className='bg-white br-50p p-absolute' css={DOT_CSS[`${tone}-${leftPercent}`]} />
        ))}
      </Container>
      <Container className='flex flex-column flex-middle o-hidden' css={CSS_STOP_LABEL_WRAP}>
        <Typography
          className={stopLabelClassName}
          color='subheading'
          componentNode='p'
          style={STOP_LABEL_STYLE}
          variant='B3'
        >
          {stop.label}
        </Typography>
        {showUnderline && <Container css={TUPLE_HOVER_UNDERLINE_CSS} />}
      </Container>
    </Container>
  )

  if (!layovers.length) return body

  const tooltipContent = (
    <Container className='flex flex-column' css={{ gap: 12 }}>
      {layovers.map((layover, index) => {
        const stopNumber = index + 1
        const hours = layover.hours ?? 0
        const minutes = layover.minutes ?? 0
        let durationStr = ''
        if (hours > 0 && minutes > 0) durationStr = `${hours}h ${minutes}m`
        else if (hours > 0) durationStr = `${hours}h`
        else if (minutes > 0) durationStr = `${minutes}m`

        const layoverCity = layover.airportCity || layover.airportCode
        const headerText = durationStr
          ? `Stop ${stopNumber} • ${durationStr} layover at ${layoverCity}`
          : `Stop ${stopNumber} • Layover at ${layoverCity}`

        const callouts = layover.changeMessages ?? []

        return (
          <React.Fragment key={`${layover.airportCode}-${index}`}>
            {index > 0 ? (
              <Divider borderColor={TUPLE_COLORS.dividerOnDark} borderStyle='dotted' type='dotted' />
            ) : null}
            <Container className='flex flex-column' css={{ gap: 4 }}>
              <Typography className='ws-nowrap' color='neutral' componentNode='p' variant='P3'>
                {headerText}
              </Typography>
              {callouts.map((callout, cIdx) => (
                <Typography
                  key={cIdx}
                  className='ws-nowrap'
                  componentNode='p'
                  style={{ color: TUPLE_COLORS.tooltipHighlight }}
                  variant='L3'
                >
                  {callout}
                </Typography>
              ))}
            </Container>
          </React.Fragment>
        )
      })}
    </Container>
  )

  return (
    <RevampTooltip content={tooltipContent} position='top'>
      {body}
    </RevampTooltip>
  )
}

export default memo(DurationStopsBlock)
